import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

/**
 * Handles the email verification link: /verify-email/:token?role=student|university
 *
 * On mount it calls GET /api/auth/verify-email/:token.
 * - Success → stores JWT, logs the user in, redirects to their dashboard.
 * - Expired  → shows a "Resend" form.
 * - Invalid  → shows an error with a link back to signup.
 */
const VerifyEmail = () => {
  useDocumentMetadata('Verify Your Email — Dakhla', 'Confirm your email address to activate your Dakhla account.');

  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | success | expired | invalid
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '' | 'sending' | 'sent' | 'error'
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const verify = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.VERIFY_EMAIL(token));
        const data = await res.json();

        if (res.ok) {
          // Log the user in via AuthContext
          login(data.user, data.access_token);
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
          // Redirect after short delay so they can read the success message
          setTimeout(() => {
            navigate('/');
          }, 2500);
        } else if (data.expired) {
          setStatus('expired');
          setMessage(data.error);
        } else {
          setStatus('invalid');
          setMessage(data.error || 'This verification link is invalid.');
        }
      } catch {
        setStatus('invalid');
        setMessage('Something went wrong. Please try again.');
      }
    };

    verify();
  }, [token, login, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus('sending');
    try {
      const res = await fetch(API_ENDPOINTS.RESEND_VERIFICATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (res.ok) {
        setResendStatus('sent');
      } else {
        setResendStatus('error');
        setMessage(data.error || 'Could not resend. Please try again.');
      }
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10">

        {/* Header logo */}
        <div className="flex justify-center mb-6">
          <img src="/assets/logos/dakhla-logo-urdu.svg" alt="Dakhla Logo" className="h-12" />
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div className="text-center">
            <DakhlaButterflyLoader text="Verifying your email…" />
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="text-center">
            <CheckCircleIcon className="h-14 w-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-extrabold text-slate-800 mb-2">Email Verified!</h1>
            <p className="text-sm text-slate-500">{message}</p>
            <p className="mt-3 text-xs text-slate-400">Redirecting you to homepage…</p>
          </div>
        )}

        {/* Expired — show resend form */}
        {status === 'expired' && (
          <div className="text-center">
            <ArrowPathIcon className="h-14 w-14 text-amber-400 mx-auto mb-4" />
            <h1 className="text-xl font-extrabold text-slate-800 mb-2">Link Expired</h1>
            <p className="text-sm text-slate-500 mb-6">{message}</p>

            {resendStatus === 'sent' ? (
              <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-700 font-medium">
                ✓ A new verification link has been sent to your inbox.
              </div>
            ) : (
              <form onSubmit={handleResend} className="text-left space-y-3">
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  Enter your email to get a new link
                </label>
                <input
                  type="email"
                  required
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {resendStatus === 'error' && (
                  <p className="text-xs text-red-500">{message || 'Failed to resend. Please try again.'}</p>
                )}
                <button
                  type="submit"
                  disabled={resendStatus === 'sending'}
                  className="w-full btn-primary py-2.5 text-sm disabled:opacity-60"
                >
                  {resendStatus === 'sending' ? 'Sending…' : 'Resend Verification Email'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Invalid */}
        {status === 'invalid' && (
          <div className="text-center">
            <XCircleIcon className="h-14 w-14 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-extrabold text-slate-800 mb-2">Invalid Link</h1>
            <p className="text-sm text-slate-500 mb-6">{message}</p>
            <Link
              to="/signup"
              className="btn-primary text-sm inline-block px-6 py-2.5"
            >
              Back to Sign Up
            </Link>
          </div>
        )}

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-400">
          Need help?{' '}
          <a href="mailto:support.dakhla@gmail.com" className="text-primary-600 font-semibold hover:underline">
            support.dakhla@gmail.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmail;
